'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import { Editor } from '@tiptap/core';
import { POLL_TIME_THREE_SECONDS } from 'constants/constants';
import {
  ADDITIONAL_INSTRUCTIONS_DEFAULT,
  CONTENT_FORMAT_DEFAULT,
  CONTENT_LENGTH_DEFAULT,
  CONTENT_TYPE_DEFAULT,
  CONTEXT_IDS_DEFAULT,
  LANGUAGE_DEFAULT,
  STYLE_GUIDE_DEFAULT,
  TITLE_DEFAULT,
  TOPIC_DEFAULT
} from 'constants/defaults';
import {
  ContentSourceEnum,
  ContextLibraryUploadedFileType,
  ErrorCodeEnum,
  GeneratedContent,
  GeneratedContentCustomPromptInput,
  GeneratedContentCustomPromptPayload,
  GeneratedContentFormatEnum,
  GeneratedContentImproveWritingInput,
  GeneratedContentImproveWritingPayload,
  GeneratedContentMakeLongerInput,
  GeneratedContentMakeLongerPayload,
  GeneratedContentMakeShorterInput,
  GeneratedContentMakeShorterPayload,
  GeneratedContentStatusEnum,
  GeneratedContentStyleGuideInput,
  GeneratedContentTypeEnum,
  IntegrationEnum,
  Maybe,
  PaymentMethodEnum,
  QualityEnum
} from 'gql/graphql';
import { toaster } from 'helpers/toaster';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { useClientAccount } from 'providers/ClientProviders/useClientAccount';
import { useContentGenerationAsk } from 'providers/ContentGenerationProviders/useContentGenerationAsk';
import { useContentGenerationCreate } from 'providers/ContentGenerationProviders/useContentGenerationCreate';
import { useContentGenerationData } from 'providers/ContentGenerationProviders/useContentGenerationData';
import { useContentGenerationExpand } from 'providers/ContentGenerationProviders/useContentGenerationExpand';
import { useContentGenerationImprove } from 'providers/ContentGenerationProviders/useContentGenerationImprove';
import { useContentGenerationNodesCount } from 'providers/ContentGenerationProviders/useContentGenerationNodesCount';
import { useContentGenerationShorten } from 'providers/ContentGenerationProviders/useContentGenerationShorten';
import { useContentGenerationUpdate } from 'providers/ContentGenerationProviders/useContentGenerationUpdate';
import { useContentUpdate } from 'providers/ContentGenerationProviders/useContentUpdate';
import { useContextLibraryDataAll } from 'providers/ContextLibraryProviders/useContextLibraryDataAll';
import { useTranslationRequestCreate } from 'providers/TranslationRequests/useTranslationRequestCreate';
import {
  ChangeEvent,
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Control, FieldErrors, useForm, useWatch } from 'react-hook-form';
import { notifyException } from 'services/appsignal';
import { ESpansException } from 'types/ESpans';
import { KeysEnum } from 'types/KeysEnum';
import { Option } from 'types/Option';
import { TGenericError } from 'types/TGenericError';
import { TScheme } from 'types/TScheme';
import * as yup from 'yup';
import useGenerationContent, {
  StyleGuidesContentGenerationForm
} from './useOptionsGenerationContent';

type TContentGenerationContext = {
  contentType: string;
  targetLanguage: string;
  optionsContexts: Option[];
  optionsContentType: Option[];
  optionsContentFormat: Option[];
  optionsLanguages: Option[];
  optionsFilterTags: Option[];
  optionsStyleGuidesCardFields: {
    label: string;
    placeholder: string;
    name: keyof StyleGuidesContentGenerationForm;
    options: Option[];
  }[];
  contextsDisabled: boolean;
  contextIds: string[];
  selectedTagId: string;
  contextsIncluded?: ContextLibraryUploadedFileType[];
  isGeneratingContent: boolean;
  isSavingChanges: boolean;
  isCreatingTranslation: boolean;
  isNotInitialized: boolean;
  generatedContent: GeneratedContent;
  words?: number;
  control: Control<InputContentGenerationForm, any>;
  errors: FieldErrors<InputContentGenerationForm>;
  isImproving?: boolean;
  isChecking?: boolean;
  styleGuide: GeneratedContentStyleGuideInput | undefined;
  handleTitle: (e: ChangeEvent<HTMLInputElement>) => void;
  handleSelectContextIds: (values: string[]) => void;
  handleDeleteContextIds: (id: string) => () => void;
  setSelectedTagId: Dispatch<SetStateAction<string>>;
  handleGenerateContent: () => void;
  handleSaveChanges: () => void;
  handleAiTranslation: () => void;
  handleCreateNewTranslation: () => void;
  handleDuplicate: () => void;
  handleRegenerate: () => void;
  handleChangeTargetLanguage: (targetLanguageIncoming: string) => () => void;
  handleSelectStyleGuides: (name: string) => (value: Option) => void;
  handleChangeWordsCount: () => void;
  goCreateNewTranslation: () => void;
  contentGenerationAsk: UseMutateAsyncFunction<
    GeneratedContentCustomPromptPayload,
    TGenericError,
    GeneratedContentCustomPromptInput,
    unknown
  >;
  contentGenerationImprove: UseMutateAsyncFunction<
    GeneratedContentImproveWritingPayload,
    TGenericError,
    GeneratedContentImproveWritingInput,
    unknown
  >;
  contentGenerationShorten: UseMutateAsyncFunction<
    GeneratedContentMakeShorterPayload,
    TGenericError,
    GeneratedContentMakeShorterInput,
    unknown
  >;
  contentGenerationExpand: UseMutateAsyncFunction<
    GeneratedContentMakeLongerPayload,
    TGenericError,
    GeneratedContentMakeLongerInput,
    unknown
  >;
};

export interface InputContentGenerationForm extends StyleGuidesContentGenerationForm {
  topic: string;
  language: string;
  contentType: GeneratedContentTypeEnum;
  contentFormat: GeneratedContentFormatEnum;
  title?: string;
  contentLength?: number;
  additionalInstructions?: string;
  styleGuide?: GeneratedContentStyleGuideInput;
  contextIds: string[];
}

const initialState: TContentGenerationContext = {
  contentType: '',
  targetLanguage: '',
  optionsLanguages: [],
  optionsContexts: [],
  optionsContentType: [],
  optionsContentFormat: [],
  optionsFilterTags: [],
  optionsStyleGuidesCardFields: [],
  contextsDisabled: false,
  contextIds: [],
  selectedTagId: '',
  contextsIncluded: [],
  isGeneratingContent: false,
  isSavingChanges: false,
  isCreatingTranslation: false,
  isNotInitialized: false,
  words: 0,
  generatedContent: {} as GeneratedContent,
  control: {} as Control<InputContentGenerationForm, any>,
  errors: {} as FieldErrors<InputContentGenerationForm>,
  isImproving: false,
  isChecking: false,
  styleGuide: undefined,
  handleTitle: () => {},
  goCreateNewTranslation: () => {},
  handleGenerateContent: () => {},
  handleSelectContextIds: () => {},
  handleDeleteContextIds: () => () => {},
  handleDuplicate: () => () => {},
  setSelectedTagId: () => {},
  handleSaveChanges: () => {},
  handleAiTranslation: () => {},
  handleCreateNewTranslation: () => {},
  handleRegenerate: () => {},
  handleChangeTargetLanguage: () => () => {},
  handleSelectStyleGuides: () => () => {},
  handleChangeWordsCount: () => {},
  contentGenerationAsk: async () => ({} as GeneratedContentCustomPromptPayload),
  contentGenerationImprove: async () => ({} as GeneratedContentImproveWritingPayload),
  contentGenerationShorten: async () => ({} as GeneratedContentMakeShorterPayload),
  contentGenerationExpand: async () => ({} as GeneratedContentMakeLongerPayload)
};

const ContentGenerationContext = createContext<TContentGenerationContext>(initialState);

const useContentGenerationContext = (): TContentGenerationContext =>
  useContext(ContentGenerationContext);

interface IContentGenerationProvider {
  defaultGeneratedContent?: GeneratedContent;
  editor: Editor | null;
  refetch?: () => void;
  open: boolean;
  copyGeneratedContent?: Partial<GeneratedContent>;
  setCopyGeneratedContent?: Dispatch<SetStateAction<Partial<GeneratedContent>>>;
  children: ({
    title,
    handleTitle,
    generatedContent,
    words,
    isPending
  }: {
    title?: string;
    handleTitle: (e: ChangeEvent<HTMLInputElement>) => void;
    generatedContent: GeneratedContent;
    words: number;
    isPending: boolean;
  }) => ReactNode;
}

const ContentGenerationProvider: FC<IContentGenerationProvider> = ({
  defaultGeneratedContent,
  editor,
  open,
  setCopyGeneratedContent,
  copyGeneratedContent,
  refetch: refetchList,
  children
}): JSX.Element => {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();

  const schema = yup.object().shape({
    topic: yup.string().required(t('messages.fields.title_required')),
    language: yup.string().required(t('messages.fields.target_language_required')),
    contentType: yup.string().required(t('messages.fields.content_type_required')),
    contentFormat: yup.string().when('contentType', {
      is: (value: string | undefined) => !!value,
      then: yup.string().required(t('messages.fields.content_format_required')),
      otherwise: yup.string().optional()
    }),
    title: yup.string().optional(),
    contentLength: yup.number().optional(),
    additionalInstructions: yup.string().optional(),
    styleGuide: yup.object().shape({
      culturalConsideration: yup.string().nullable().notRequired(),
      formality: yup.string().nullable().notRequired(),
      purposeOfText: yup.string().nullable().notRequired(),
      style: yup.string().nullable().notRequired(),
      targetAudience: yup.string().nullable().notRequired(),
      toneOfVoice: yup.string().nullable().notRequired()
    }),
    contextIds: yup.array().optional()
  } as TScheme<InputContentGenerationForm>);
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
    handleSubmit
  } = useForm<InputContentGenerationForm>({
    resolver: yupResolver(schema)
  });

  const [targetLanguage, setTargetLanguage] = useState('');
  const [words, setWords] = useState(0);

  const [selectedTagId, setSelectedTagId] = useState('');

  const [generatedContent, setGeneratedContent] = useState<GeneratedContent>(
    {} as GeneratedContent
  );

  const topic = useWatch<InputContentGenerationForm, typeof KeysEnum.Topic>({
    control,
    name: KeysEnum.Topic,
    defaultValue: TOPIC_DEFAULT
  });

  const language = useWatch<InputContentGenerationForm, typeof KeysEnum.Language>({
    control,
    name: KeysEnum.Language,
    defaultValue: LANGUAGE_DEFAULT
  });

  const contentType = useWatch<InputContentGenerationForm, typeof KeysEnum.ContentType>({
    control,
    name: KeysEnum.ContentType,
    defaultValue: CONTENT_TYPE_DEFAULT as unknown as undefined
  });

  const contentFormat = useWatch<InputContentGenerationForm, typeof KeysEnum.ContentFormat>({
    control,
    name: KeysEnum.ContentFormat,
    defaultValue: CONTENT_FORMAT_DEFAULT
  });

  const title = useWatch<InputContentGenerationForm, typeof KeysEnum.Title>({
    control,
    name: KeysEnum.Title,
    defaultValue: TITLE_DEFAULT
  });

  const contentLength = useWatch<InputContentGenerationForm, typeof KeysEnum.ContentLength>({
    control,
    name: KeysEnum.ContentLength,
    defaultValue: CONTENT_LENGTH_DEFAULT
  });

  const additionalInstructions = useWatch<
    InputContentGenerationForm,
    typeof KeysEnum.AdditionalInstructions
  >({
    control,
    name: KeysEnum.AdditionalInstructions,
    defaultValue: ADDITIONAL_INSTRUCTIONS_DEFAULT
  });

  const styleGuide = useWatch<InputContentGenerationForm, typeof KeysEnum.StyleGuide>({
    control,
    name: KeysEnum.StyleGuide,
    defaultValue: STYLE_GUIDE_DEFAULT
  });

  const contextIds = useWatch<InputContentGenerationForm, typeof KeysEnum.ContextIds>({
    control,
    name: KeysEnum.ContextIds,
    defaultValue: CONTEXT_IDS_DEFAULT
  });

  const { data: contexts } = useContextLibraryDataAll();

  const {
    optionsLanguages,
    optionsContexts,
    optionsContentType,
    optionsContentFormat,
    optionsFilterTags,
    optionsStyleGuidesCardFields
  } = useGenerationContent({
    contentType,
    contextIds,
    selectedTagId
  });

  const { data, isPending, isLoading, refetch } = useContentGenerationData(
    { id: generatedContent?.id },
    {
      enabled: !!generatedContent?.id,
      staleTime: 1,
      refetchInterval:
        generatedContent?.status === GeneratedContentStatusEnum.Pending
          ? POLL_TIME_THREE_SECONDS
          : false
    }
  );

  const { data: clientAccount } = useClientAccount();
  const { refetch: refetchNodesCount } = useContentGenerationNodesCount();

  const {
    mutateAsync: contentGenerationUpdate,
    isPending: isSavingChangesContentGenerationUpdate
  } = useContentGenerationUpdate();

  const { mutateAsync: contentUpdate, isPending: isSavingChangesContentUpdate } =
    useContentUpdate();

  const { mutateAsync: contentGenerationCreate, isPending: isGeneratingContent } =
    useContentGenerationCreate({
      onSuccess: () =>
        toaster.success(
          t('messages.success.content_generated.paragraph'),
          t('messages.success.content_generated.title')
        ),
      onError: (error: any) => {
        notifyException(error, ESpansException.ContentGenerationException);
        if (error?.errorCode === ErrorCodeEnum.PaymentRequired)
          toaster.error(
            clientAccount?.paymentMethod === PaymentMethodEnum.CreditCard ||
              clientAccount?.paymentMethod === PaymentMethodEnum.BankTransfer
              ? t.raw('messages.error.ai_trial.paragraph.confirmed')
              : t.raw('messages.error.ai_trial.paragraph.unconfirmed'),
            t('messages.error.ai_trial.title')
          );
        else
          toaster.error(
            t('messages.error.content_generated.paragraph'),
            t('messages.error.content_generated.title')
          );
      }
    });

  const { mutateAsync: contentGenerationImprove, isPending: isGeneratingContentImprove } =
    useContentGenerationImprove({
      onError: (error: any) => {
        notifyException(error, ESpansException.ContentGenerationException);
        toaster.error(t('messages.error.improve_content_generation'));
      }
    });

  const { mutateAsync: contentGenerationAsk, isPending: isGeneratingContentAsk } =
    useContentGenerationAsk({
      onError: (error: any) => {
        notifyException(error, ESpansException.ContentGenerationException);
        toaster.error(t('messages.error.ask_content_generation'));
      }
    });

  const { mutateAsync: contentGenerationShorten, isPending: isGeneratingContentShorten } =
    useContentGenerationShorten({
      onError: (error: any) => {
        notifyException(error, ESpansException.ContentGenerationException);
        toaster.error(t('messages.error.shorten_content_generation'));
      }
    });

  const { mutateAsync: contentGenerationExpand, isPending: isGeneratingContentExpand } =
    useContentGenerationExpand({
      onError: (error: any) => {
        notifyException(error, ESpansException.ContentGenerationException);
        toaster.error(t('messages.error.expand_content_generation'));
      }
    });

  const { mutateAsync: translationRequestCreate, isPending: isCreatingTranslation } =
    useTranslationRequestCreate({
      onSuccess: () => toaster.success(t('messages.success.create_new_tr')),
      onError: (error: unknown) => {
        notifyException(error, ESpansException.ContentGenerationException);
        toaster.error(t('messages.error.create_new_tr'));
      }
    });

  const contextsDisabled = !optionsContexts?.length;

  const contextsIncluded = useMemo(
    () => contexts?.nodes?.filter(({ id }) => contextIds.includes(id)),
    [contexts?.nodes, contextIds]
  );

  const isChecking = isLoading || isPending;
  const isImproving =
    isGeneratingContentImprove ||
    isGeneratingContentAsk ||
    isGeneratingContentShorten ||
    isGeneratingContentExpand;

  const isNotInitialized = !generatedContent?.id;

  const isSavingChanges = isSavingChangesContentGenerationUpdate || isSavingChangesContentUpdate;

  const initializeStates = useCallback(
    (generatedContent?: Maybe<GeneratedContent>) => {
      if (generatedContent) {
        setValue(KeysEnum.Topic, generatedContent?.topic ?? '');
        setValue(KeysEnum.Language, generatedContent?.languages?.at(0) ?? '');
        setValue(KeysEnum.ContentType, generatedContent?.contentType ?? '');
        setValue(KeysEnum.ContentFormat, generatedContent?.contentFormat ?? '');
        setValue(KeysEnum.ContentLength, Number(generatedContent?.contentLength ?? 0) ?? 0);
        setValue(KeysEnum.StyleGuide, generatedContent?.styleGuide ?? undefined);
        setValue(KeysEnum.AdditionalInstructions, generatedContent?.additionalInstructions ?? '');
        setGeneratedContent(generatedContent);
        setTargetLanguage(generatedContent?.generations?.at(0)?.targetLanguage ?? '');
        setWords(generatedContent?.generations?.at(0)?.words ?? 0);
        setValue(KeysEnum.Title, generatedContent?.title ?? '');

        editor?.commands.setContent(generatedContent?.generations?.at(0)?.content ?? '');
      }
    },
    [editor, setValue, setGeneratedContent]
  );

  const handleSelectContextIds = useCallback(
    (values: string[]) => values && setValue(KeysEnum.ContextIds, [...values]),
    [setValue]
  );

  const handleSelectStyleGuides = useCallback(
    (name: string) => (option: Option) => {
      if (option.value)
        return setValue(KeysEnum.StyleGuide, { ...styleGuide, [name]: option.value });

      return setValue(KeysEnum.StyleGuide, { ...styleGuide, [name]: undefined });
    },
    [setValue, styleGuide]
  );

  const handleDeleteContextIds = useCallback(
    (id: string) => () =>
      setValue(
        KeysEnum.ContextIds,
        contextIds?.filter((contextId) => contextId !== id)
      ),
    [setValue, contextIds]
  );

  const handleTitle = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => setValue(KeysEnum.Title, target.value),
    [setValue]
  );

  const handleRegenerate = () => {};

  const handleAiTranslation = () => {};

  const handleDuplicate = useCallback(() => {
    setCopyGeneratedContent?.({
      title: '',
      topic,
      languages: [language],
      contentType,
      contentFormat,
      contentLength: Number(contentLength),
      styleGuide,
      additionalInstructions,
      createdAt: '',
      id: ''
    });
    router.push('/content-creator/new/-1');
  }, [
    router,
    setGeneratedContent,
    title,
    topic,
    language,
    contentType,
    contentFormat,
    contentLength,
    contextIds,
    styleGuide,
    additionalInstructions
  ]);

  const handleCreateNewTranslation = useCallback(
    () =>
      translationRequestCreate({
        copy: editor?.getHTML(),
        contentSource: ContentSourceEnum.Copy,
        quality: QualityEnum.Localization,
        name: title,
        sourceLanguageCode: 'en',
        styleGuideReferenceId: styleGuide ? String(styleGuide) : undefined,
        targetLanguageCodes: [language],
        quote: false
      }),
    [editor, title, language, styleGuide]
  );

  const goCreateNewTranslation = useCallback(() => {
    if (generatedContent?.id)
      router.push(`/translation/new/${IntegrationEnum.ContentCreator}?id=${generatedContent?.id}`);
  }, [router, generatedContent?.id]);

  const onGenerateContent = useCallback(
    async (values: InputContentGenerationForm) => {
      const contentGenerationNew = await contentGenerationCreate({
        generatedContent: {
          title: values.title,
          topic: values.topic,
          languages: [values.language],
          contentType: values.contentType,
          contentFormat: values.contentFormat,
          contentLength: Number(values.contentLength),
          contextIds: values.contextIds,
          additionalInstructions: values.additionalInstructions,
          styleGuide: values.styleGuide
        }
      });
      refetchNodesCount();
      refetchList?.();
      initializeStates(contentGenerationNew?.generatedContent);
    },
    [refetchList, refetchNodesCount, contentGenerationCreate, initializeStates]
  );

  const handleGenerateContent = handleSubmit(onGenerateContent);

  const onSaveChanges = useCallback(
    async (values: InputContentGenerationForm) => {
      try {
        if (generatedContent.generations) {
          setGeneratedContent((oldState) => ({
            ...oldState,
            contentLength: Number(oldState.contentLength),
            generations: oldState.generations?.map((generation) =>
              generation.targetLanguage === targetLanguage
                ? {
                    ...generation,
                    content: editor?.getHTML()
                  }
                : generation
            )
          }));

          await Promise.all([
            ...generatedContent.generations.map((generation) =>
              contentGenerationUpdate({
                generatedContentGenerationId: generation.id,
                content:
                  (generation.targetLanguage === targetLanguage
                    ? editor?.getHTML()
                    : generation.content) ?? ''
              })
            ),
            contentUpdate({
              generatedContentId: generatedContent.id,
              generatedContent: {
                title: values.title,
                topic: values.topic,
                languages: [values.language],
                contentType: values.contentType,
                contentFormat: values.contentFormat,
                contentLength: Number(values.contentLength),
                contextIds: values.contextIds,
                additionalInstructions: values.additionalInstructions,
                styleGuide: values.styleGuide
              }
            })
          ]);
          refetchList?.();
          refetch();
          toaster.success(t('messages.success.update_content_generation'));
        }
      } catch (error) {
        notifyException(error, ESpansException.ContentGenerationException);
        toaster.error(t('messages.error.update_content_generation'));
      }
    },
    [
      generatedContent,
      editor,
      targetLanguage,
      t,
      contentGenerationCreate,
      initializeStates,
      setGeneratedContent,
      refetch,
      refetchList,
      contentGenerationUpdate
    ]
  );

  const handleSaveChanges = handleSubmit(onSaveChanges);

  const handleChangeTargetLanguage = useCallback(
    (targetLanguageIncoming: string) => () => {
      setGeneratedContent((oldState) => ({
        ...oldState,
        generations: oldState.generations?.map((generation) =>
          generation.targetLanguage === targetLanguage
            ? {
                ...generation,
                content: editor?.getHTML()
              }
            : generation
        )
      }));
      const generatedContentFound = generatedContent.generations?.find(
        (generation) => generation.targetLanguage === targetLanguageIncoming
      );
      setTargetLanguage(generatedContentFound?.targetLanguage ?? '');
      setWords(generatedContentFound?.words ?? 0);
      editor?.commands.setContent(generatedContentFound?.content ?? '');
    },
    [targetLanguage, setTargetLanguage, generatedContent, editor]
  );

  const handleChangeWordsCount = useCallback(() => {
    setWords(editor?.getText()?.replace(/\n/g, ' ').split(/\s+/).filter(Boolean)?.length ?? 0);
  }, [editor]);

  useEffect(() => {
    const generateContentNew = data?.nodes?.at(0);
    if (generateContentNew?.status === GeneratedContentStatusEnum.Ready)
      initializeStates(generateContentNew);
  }, [data, initializeStates]);

  useEffect(() => {
    if (defaultGeneratedContent && !generatedContent.id)
      initializeStates((defaultGeneratedContent ?? {}) as GeneratedContent);
  }, [open, defaultGeneratedContent, generatedContent?.id, initializeStates]);

  useEffect(() => {
    if (copyGeneratedContent && !generatedContent.id && copyGeneratedContent.id === '-1') {
      const { id, ...restCopyGeneratedContent } = copyGeneratedContent;
      initializeStates(restCopyGeneratedContent as GeneratedContent);
    }
  }, [open, copyGeneratedContent, generatedContent?.id, initializeStates]);

  useEffect(() => {
    if (
      generatedContent.id &&
      generatedContent.status === GeneratedContentStatusEnum.Ready &&
      !isChecking &&
      !pathname.match(/^(\/[a-z]{2})?\/content-creator\/(\d+)?$/)
    )
      router.push(`/content-creator/${generatedContent.id}`);
  }, [generatedContent?.id, generatedContent.status, router, isChecking, pathname]);

  return (
    <ContentGenerationContext.Provider
      value={{
        contentType,
        targetLanguage,
        optionsLanguages,
        optionsContexts,
        optionsContentType,
        optionsContentFormat,
        optionsFilterTags,
        contextsDisabled,
        optionsStyleGuidesCardFields,
        contextIds,
        selectedTagId,
        contextsIncluded,
        isGeneratingContent,
        isSavingChanges,
        isCreatingTranslation,
        isNotInitialized,
        generatedContent,
        words,
        control,
        errors,
        isImproving,
        isChecking,
        styleGuide,
        handleTitle,
        goCreateNewTranslation,
        handleSelectContextIds,
        handleDeleteContextIds,
        setSelectedTagId,
        handleGenerateContent,
        handleSaveChanges,
        handleAiTranslation,
        handleCreateNewTranslation,
        handleRegenerate,
        handleDuplicate,
        handleChangeTargetLanguage,
        handleChangeWordsCount,
        handleSelectStyleGuides,
        contentGenerationAsk,
        contentGenerationImprove,
        contentGenerationShorten,
        contentGenerationExpand
      }}
    >
      {children({
        title,
        handleTitle,
        generatedContent,
        words,
        isPending:
          (isPending && !!generatedContent?.id) ||
          generatedContent?.status === GeneratedContentStatusEnum.Pending
      })}
    </ContentGenerationContext.Provider>
  );
};

export { ContentGenerationProvider, useContentGenerationContext };
